import {announce} from '@github-ui/aria-live'
import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {TokenType} from '@github-ui/filter-query'
import {hpcAttrs} from '@github-ui/hpc'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {getQueryClient} from '@github-ui/query-client'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as PreloadableLink, matchPath, useLocation, useNavigation} from '@github-ui/react-router'
import {PullsList, type PullsListSelection} from '@github-ui/shared-dashboard-lists/pulls/PullsList'
import type {EnrichedPullListItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import type {StateFilter} from '@github-ui/shared-dashboard-lists/shared/types/list-types'
import {
  addStateToQueryWithLegacyAliases,
  analyzeStateQuery,
} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addLabelFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'
import {sendCustomMetric} from '@github-ui/stats'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {Pagination} from '@primer/react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject} from 'react'

import {RepoPullsBulkLabels} from '../components/RepoPullsBulkLabels'
import {RepoPullsBulkMarkAs} from '../components/RepoPullsBulkMarkAs'
import {
  RepoPullsBulkAssignees,
  RepoPullsBulkMilestone,
  RepoPullsBulkProjects,
} from '../components/RepoPullsBulkMetadata'
import {getPullRequestTitleLinkProps} from '../helpers/get-pull-request-title-link-props'
import type {RepoPullsBulkUpdate} from '../helpers/repo-pulls-bulk-update'
import {useRepoPullsDashboardData, useRepoPullsSearch} from '../hooks/use-repo-pulls-dashboard'
import {useRepoPullsBulkUpdate} from '../mutations/use-repo-pulls-bulk-update'
import {repoPullsQueryResolver} from './repo-pulls-dashboard-content-route'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import type {RepoPullsQuickFilterKind} from './repo-pulls-quick-filters/quick-filter-shared'
import {getRepoPullsFilterState} from './repo-pulls-quick-filters/quick-filter-query'
import {useRepoPullsQuickFilterItems} from './repo-pulls-quick-filters/use-repo-pulls-quick-filter-actions'
import {getReviewFilterState, replaceReviewFilter} from './repo-pulls-quick-filters/ReviewQuickFilter'

const REPO_PULLS_DEFAULT_SORTING_KEY = 'created'
const EMPTY_PULLS: EnrichedPullListItem[] = []
type RepoPullsFilterKind = RepoPullsQuickFilterKind | 'sort'
type RepoPullsFilterSource = 'list' | 'toolbar'

const FILTER_KEYS: Record<Exclude<RepoPullsFilterKind, 'sort'>, readonly TokenType[]> = {
  assignee: ['assignee'],
  author: ['author'],
  label: ['label'],
  milestone: ['milestone'],
  projects: ['project'],
  reviews: ['review', 'reviewed-by', 'review-requested', 'user-review-requested'],
}

function getFilterState(
  query: string,
  filter: RepoPullsFilterKind,
  structuralUpdatesEnabled: boolean,
): string | undefined {
  if (filter === 'sort') return getSortFromQuery(query)
  if (filter === 'reviews') return getReviewFilterState(query, structuralUpdatesEnabled)

  return getRepoPullsFilterState(query, FILTER_KEYS[filter], structuralUpdatesEnabled)
}

function hasSameFilterState(
  currentQuery: string,
  nextQuery: string,
  filter: RepoPullsFilterKind,
  structuralUpdatesEnabled: boolean,
) {
  const currentState = getFilterState(currentQuery, filter, structuralUpdatesEnabled)
  const nextState = getFilterState(nextQuery, filter, structuralUpdatesEnabled)
  return currentState === undefined || nextState === undefined ? currentQuery === nextQuery : currentState === nextState
}

const EMPTY_SELECTED_PULL_NUMBERS = new Set<number>()
const OPTIMISTIC_CLOSE_OVERLAY_DURATION_MS = 60_000
const PENDING_NAVIGATION_COMMIT_TIMEOUT_MS = 5_000

type OptimisticCloseBatch = {
  id: number
  membershipContextKey: string
  selectionContextKey: string
  pullNumbers: Set<number>
  expiresAt?: number
}

type BulkUpdateProgress = {
  abortController: AbortController
  message: string
  percentage?: number
}

type RepoPullsContentCacheData = {
  payload?: {
    results?: Array<{number?: number}>
  }
}

function isSameLocation(a: {pathname: string; search: string}, b: {pathname: string; search: string}) {
  return a.pathname === b.pathname && a.search === b.search
}

function isPreserveListNavigation(state: unknown) {
  return Boolean(
    state && typeof state === 'object' && 'preserveList' in state && (state as {preserveList?: unknown}).preserveList,
  )
}

function addRepoStateToQuery(query: string, state: StateFilter) {
  return addStateToQueryWithLegacyAliases(query, state)
}

function getSelectionContextKey({
  owner,
  repo,
  page,
  query,
  milestone,
}: {
  owner: string
  repo: string
  page: number
  query: string
  milestone: string
}) {
  return `${owner}/${repo}:${page}:${query}:${milestone}`
}

export function RepoPullsDashboardContent() {
  const location = useLocation()
  const navigation = useNavigation()
  const {clearPersistedToast} = useToastContext()
  const bulkUpdateAbortControllersRef = useRef(new Set<AbortController>())
  const bulkUpdateOperationIdRef = useRef(0)
  const bulkUpdateProgressRef = useRef(new Map<number, BulkUpdateProgress>())
  const navigationLocation = navigation.location ?? location
  const currentDashboardMatch = matchPath('/:owner/:repo/pulls/*', location.pathname)
  const navigationDashboardMatch = matchPath('/:owner/:repo/pulls/*', navigationLocation.pathname)
  const isLeavingDashboard =
    !navigationDashboardMatch ||
    navigationDashboardMatch.params.owner !== currentDashboardMatch?.params.owner ||
    navigationDashboardMatch.params.repo !== currentDashboardMatch?.params.repo
  const isNavigating = Boolean(
    navigation.location &&
    matchPath('/:owner/:repo/pulls/*', navigation.location.pathname) &&
    !isSameLocation(navigation.location, location),
  )
  const isListLoading = isNavigating && !isPreserveListNavigation(navigation.location?.state)
  const navigationSearchParams = navigation.location ? new URLSearchParams(navigation.location.search) : undefined
  const navigationSelectionContextKey =
    navigation.location &&
    navigationDashboardMatch?.params.owner &&
    navigationDashboardMatch.params.repo &&
    navigationSearchParams
      ? getSelectionContextKey({
          owner: navigationDashboardMatch.params.owner,
          repo: navigationDashboardMatch.params.repo,
          page: Number(navigationSearchParams.get('page') || 1),
          query: repoPullsQueryResolver({
            pathname: navigation.location.pathname,
            searchParams: navigationSearchParams,
          }),
          milestone: navigationSearchParams.get('milestone') ?? '',
        })
      : undefined
  const cancelBulkUpdates = useCallback(() => {
    if (bulkUpdateAbortControllersRef.current.size === 0) return
    for (const abortController of bulkUpdateAbortControllersRef.current) abortController.abort()
    bulkUpdateAbortControllersRef.current.clear()
    bulkUpdateProgressRef.current.clear()
    clearPersistedToast()
  }, [clearPersistedToast])

  useEffect(() => {
    if (isLeavingDashboard) cancelBulkUpdates()
  }, [cancelBulkUpdates, isLeavingDashboard])

  useEffect(() => cancelBulkUpdates, [cancelBulkUpdates])

  return (
    <ErrorBoundary
      boundaryName="RepoPullsDashboardContent"
      fallback={
        <div className="container-xl px-3 px-md-4 px-lg-5 tmp-mt-2 tmp-mb-3">
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        </div>
      }
    >
      <RepoPullsDashboardPage
        key={`${currentDashboardMatch?.params.owner}/${currentDashboardMatch?.params.repo}`}
        bulkUpdateAbortControllersRef={bulkUpdateAbortControllersRef}
        bulkUpdateOperationIdRef={bulkUpdateOperationIdRef}
        bulkUpdateProgressRef={bulkUpdateProgressRef}
        isNavigating={isNavigating}
        navigationSelectionContextKey={navigationSelectionContextKey}
        isListLoading={isListLoading}
      />
    </ErrorBoundary>
  )
}

function RepoPullsDashboardPage({
  bulkUpdateAbortControllersRef,
  bulkUpdateOperationIdRef,
  bulkUpdateProgressRef,
  isNavigating,
  navigationSelectionContextKey,
  isListLoading,
}: {
  bulkUpdateAbortControllersRef: RefObject<Set<AbortController>>
  bulkUpdateOperationIdRef: RefObject<number>
  bulkUpdateProgressRef: RefObject<Map<number, BulkUpdateProgress>>
  isNavigating: boolean
  navigationSelectionContextKey: string | undefined
  isListLoading: boolean
}) {
  const structuralUpdatesEnabled = isFeatureEnabled('pull_requests_structural_quick_filter_updates')
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const {contentQueryKey, error, isDeferredLoading, pageInfo, pulls, stateCounts} = useRepoPullsDashboardData()
  const {
    analyticsView,
    currentPage,
    fetchedQuery,
    getPageHref,
    hrefForQuery,
    milestone,
    navigateToResults,
    owner,
    repo,
  } = useRepoPullsSearch()
  const {sendAnalyticsEvent} = useAnalytics()
  const {addPersistedToast, addToast, clearPersistedToast} = useToastContext()
  useEffect(
    () => () => {
      for (const abortController of bulkUpdateAbortControllersRef.current) abortController.abort()
    },
    [bulkUpdateAbortControllersRef],
  )
  const initialViewRef = useRef(analyticsView)
  const sentPageViewRef = useRef(false)
  const compactMode = layoutPayload?.compactMode ?? false
  const stateQueryAnalysis = analyzeStateQuery(fetchedQuery, {legacyAliases: true})
  const selectedState = stateQueryAnalysis.kind === 'selected' ? stateQueryAnalysis.state : undefined
  const optimisticCloseEnabled = isFeatureEnabled('prs_optimistic_bulk_close_rows')
  const canBulkUpdate = layoutPayload?.actions?.canBulkUpdate ?? false
  const canBulkAddToProjects = layoutPayload?.actions?.canBulkAddToProjects ?? false
  const canBulkArchive = layoutPayload?.actions?.canBulkArchive ?? false
  const pullNumberKey = pulls
    .map(pull => pull.number)
    .sort((a, b) => a - b)
    .join(',')
  const membershipContextKey = `${owner}/${repo}:${addSortToQuery(fetchedQuery, '')}:${milestone}`
  const selectionContextKey = getSelectionContextKey({
    owner,
    repo,
    page: currentPage,
    query: fetchedQuery,
    milestone,
  })
  const selectionKey = `${selectionContextKey}:${pullNumberKey}`
  const [selection, setSelection] = useState<{key: string; pullNumbers: Set<number>}>(() => ({
    key: selectionKey,
    pullNumbers: new Set(),
  }))
  if (selection.key !== selectionKey) setSelection({key: selectionKey, pullNumbers: new Set()})
  const selectedPullNumbers = selection.key === selectionKey ? selection.pullNumbers : EMPTY_SELECTED_PULL_NUMBERS
  const optimisticCloseBatchIdRef = useRef(0)
  const pendingSelectionRestorationsRef = useRef(new Map<string, Set<number>>())
  const pendingNavigationReconciliationsRef = useRef(new Set<string>())
  const [optimisticCloseBatches, setOptimisticCloseBatches] = useState<OptimisticCloseBatch[]>([])
  const activeOptimisticClosePullNumbers = useMemo(() => {
    const pullNumbers = new Set<number>()
    for (const batch of optimisticCloseBatches) {
      for (const pullNumber of batch.pullNumbers) pullNumbers.add(pullNumber)
    }
    return pullNumbers
  }, [optimisticCloseBatches])
  const activeOptimisticallyHiddenPullNumbers = useMemo(() => {
    const pullNumbers = new Set<number>()
    for (const batch of optimisticCloseBatches) {
      if (batch.membershipContextKey !== membershipContextKey) continue
      for (const pullNumber of batch.pullNumbers) pullNumbers.add(pullNumber)
    }
    return pullNumbers.size > 0 ? pullNumbers : EMPTY_SELECTED_PULL_NUMBERS
  }, [membershipContextKey, optimisticCloseBatches])
  const hiddenPullCount = pulls.filter(pull => activeOptimisticallyHiddenPullNumbers.has(pull.number)).length
  const visiblePulls =
    hiddenPullCount > 0 ? pulls.filter(pull => !activeOptimisticallyHiddenPullNumbers.has(pull.number)) : pulls
  const visiblePageInfo =
    hiddenPullCount > 0 ? {...pageInfo, totalCount: Math.max(0, pageInfo.totalCount - hiddenPullCount)} : pageInfo
  const visibleStateCounts =
    hiddenPullCount > 0 && stateCounts
      ? {
          open: Math.max(0, stateCounts.open - hiddenPullCount),
          closed: stateCounts.closed + hiddenPullCount,
        }
      : stateCounts
  useEffect(() => {
    const nextExpiration = Math.min(
      ...optimisticCloseBatches.flatMap(batch => (batch.expiresAt === undefined ? [] : [batch.expiresAt])),
    )
    if (!Number.isFinite(nextExpiration)) return

    const timeout = window.setTimeout(
      () => {
        const now = Date.now()
        const remainingBatches = optimisticCloseBatches.filter(
          batch => batch.expiresAt === undefined || batch.expiresAt > now,
        )
        const remainingPullNumbers = new Set<number>()
        for (const batch of remainingBatches) {
          if (batch.membershipContextKey !== membershipContextKey) continue
          for (const pullNumber of batch.pullNumbers) remainingPullNumbers.add(pullNumber)
        }
        const restoredPullCount = pulls.filter(
          pull => activeOptimisticallyHiddenPullNumbers.has(pull.number) && !remainingPullNumbers.has(pull.number),
        ).length

        setOptimisticCloseBatches(batches =>
          batches.filter(batch => batch.expiresAt === undefined || batch.expiresAt > now),
        )
        if (restoredPullCount > 0) {
          announce(`${restoredPullCount} pull request${restoredPullCount === 1 ? '' : 's'} returned to the list`)
        }
      },
      Math.max(0, nextExpiration - Date.now()),
    )
    return () => window.clearTimeout(timeout)
  }, [activeOptimisticallyHiddenPullNumbers, membershipContextKey, optimisticCloseBatches, pulls])
  const removeOptimisticCloseBatch = useCallback((batchId: number | undefined) => {
    if (batchId === undefined) return
    setOptimisticCloseBatches(batches => batches.filter(batch => batch.id !== batchId))
  }, [])
  const settleOptimisticCloseBatch = useCallback((batchId: number | undefined, pullNumbers: number[]) => {
    if (batchId === undefined) return
    if (pullNumbers.length === 0) {
      setOptimisticCloseBatches(batches => batches.filter(batch => batch.id !== batchId))
      return
    }
    setOptimisticCloseBatches(batches =>
      batches.map(batch =>
        batch.id === batchId
          ? {
              ...batch,
              pullNumbers: new Set(pullNumbers),
              expiresAt: Date.now() + OPTIMISTIC_CLOSE_OVERLAY_DURATION_MS,
            }
          : batch,
      ),
    )
  }, [])
  const removePullNumbersFromOptimisticCloseBatches = useCallback((pullNumbers: number[]) => {
    if (pullNumbers.length === 0) return
    const pullNumberSet = new Set(pullNumbers)
    setOptimisticCloseBatches(batches =>
      batches.flatMap(batch => {
        const remainingPullNumbers = new Set(
          [...batch.pullNumbers].filter(pullNumber => !pullNumberSet.has(pullNumber)),
        )
        return remainingPullNumbers.size > 0
          ? [
              {
                ...batch,
                pullNumbers: remainingPullNumbers,
              },
            ]
          : []
      }),
    )
  }, [])
  const reportBulkActionError = useCallback(
    (message: string) => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({message, type: 'error', role: 'alert'})
      announce(message, {assertive: true})
    },
    [addToast],
  )
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)
  const selectionFocusFallbackRef = useRef<HTMLElement>(null)
  const restoreSelectionFocusIfLost = useCallback(() => {
    requestAnimationFrame(() => {
      const selectionFocusFallback = selectionFocusFallbackRef.current
      if (document.activeElement === selectionFocusFallback) return
      if (document.activeElement !== document.body) {
        selectionFocusFallbackRef.current = null
        return
      }

      const selectAllCheckbox = selectAllCheckboxRef.current
      if (selectAllCheckbox && !selectAllCheckbox.disabled) {
        selectionFocusFallbackRef.current = null
        selectAllCheckbox.focus()
        return
      }

      if (!optimisticCloseEnabled) return

      const focusTarget = [
        ...document.querySelectorAll<HTMLElement>(
          'h1[data-testid="header-title"], button[aria-describedby="types-collapsed-button-description"]',
        ),
      ].find(element => element.getClientRects().length > 0)
      if (!focusTarget) return
      if (focusTarget instanceof HTMLHeadingElement && !focusTarget.hasAttribute('tabindex')) {
        focusTarget.setAttribute('tabindex', '-1')
      }
      focusTarget.focus()
      selectionFocusFallbackRef.current = focusTarget
    })
  }, [optimisticCloseEnabled])
  const showRepositoryNavigation = Boolean(layoutPayload?.actions?.labels)

  useEffect(() => {
    if (sentPageViewRef.current) return
    sentPageViewRef.current = true
    sendAnalyticsEvent('pull-requests.page_view', 'REPO_PULLS_DASHBOARD', {view: initialViewRef.current})
    sendCustomMetric({
      name: 'PULL_REQUESTS_REPO_PULLS_DASHBOARD_PAGE_VIEW',
      value: 1,
      tags: {view: initialViewRef.current},
    })
  }, [sendAnalyticsEvent])

  function sendFilterAnalytics(filter: RepoPullsFilterKind, source: RepoPullsFilterSource) {
    sendAnalyticsEvent('pull-requests.click', 'FILTER_CHANGE', {filter, source})
    sendCustomMetric({name: 'PULL_REQUESTS_REPO_PULLS_DASHBOARD_FILTER_CHANGE', value: 1, tags: {filter, source}})
  }

  function applyQueryFilter({
    query,
    filter,
    source,
  }: {
    query: string
    filter: RepoPullsFilterKind
    source: RepoPullsFilterSource
  }) {
    if (hasSameFilterState(fetchedQuery, query, filter, structuralUpdatesEnabled)) return
    navigateToResults({q: query})
    sendFilterAnalytics(filter, source)
  }

  const quickFilterItems = useRepoPullsQuickFilterItems({
    owner: owner ?? '',
    repo: repo ?? '',
    query: fetchedQuery,
    onQueryChange: (query, filter) => applyQueryFilter({query, filter, source: 'toolbar'}),
    showLabels: showRepositoryNavigation,
    showMilestones: showRepositoryNavigation,
    showProjects: showRepositoryNavigation,
    structuralUpdatesEnabled,
  })

  const {emptyListState} = useScreenReaderAnnouncements(
    'pull-requests',
    isDeferredLoading,
    currentPage,
    fetchedQuery,
    REPO_PULLS_DEFAULT_SORTING_KEY,
    visiblePageInfo.totalCount,
    visiblePageInfo.totalPages,
    error ?? undefined,
    undefined,
    undefined,
  )
  const {updateCompactModeSetting} = useUpdateCompactModeSetting(compactMode, 'dashboard_surface_compact_mode', {
    persistAsPullsPreference: true,
  })

  const handlePullSelect = useCallback(
    (pullNumber: number, isSelected: boolean) => {
      setSelection(current => {
        const pullNumbers = new Set(current.key === selectionKey ? current.pullNumbers : [])
        if (isSelected) pullNumbers.add(pullNumber)
        else pullNumbers.delete(pullNumber)
        return {key: selectionKey, pullNumbers}
      })
    },
    [selectionKey],
  )

  const handleToggleSelectAll = useCallback(
    (isSelected: boolean) => {
      setSelection({
        key: selectionKey,
        pullNumbers: isSelected ? new Set(visiblePulls.map(pull => pull.number)) : new Set(),
      })
    },
    [selectionKey, visiblePulls],
  )

  const selectedPullNumberList = useMemo(() => [...selectedPullNumbers], [selectedPullNumbers])
  const hasSelectedPullWithActiveClose = selectedPullNumberList.some(pullNumber =>
    activeOptimisticClosePullNumbers.has(pullNumber),
  )
  const selectedPulls = useMemo(
    () => visiblePulls.filter(pull => selectedPullNumbers.has(pull.number)),
    [selectedPullNumbers, visiblePulls],
  )
  const currentSelectionViewRef = useRef({
    contextKey: selectionContextKey,
    contentQueryKey,
    membershipContextKey,
    selectionKey,
    authoritativePullNumbers: new Set(pulls.map(pull => pull.number)),
    optimisticCloseBatches,
  })
  const navigationSelectionContextKeyRef = useRef(navigationSelectionContextKey)
  useLayoutEffect(() => {
    currentSelectionViewRef.current = {
      contextKey: selectionContextKey,
      contentQueryKey,
      membershipContextKey,
      selectionKey,
      authoritativePullNumbers: new Set(pulls.map(pull => pull.number)),
      optimisticCloseBatches,
    }
    navigationSelectionContextKeyRef.current = navigationSelectionContextKey
    for (const pendingContextKey of pendingNavigationReconciliationsRef.current) {
      if (pendingContextKey !== selectionContextKey && pendingContextKey !== navigationSelectionContextKey) {
        pendingNavigationReconciliationsRef.current.delete(pendingContextKey)
      }
    }
  }, [
    contentQueryKey,
    membershipContextKey,
    navigationSelectionContextKey,
    optimisticCloseBatches,
    pulls,
    selectionContextKey,
    selectionKey,
  ])
  useEffect(() => {
    if (!optimisticCloseEnabled) {
      pendingNavigationReconciliationsRef.current.clear()
      return
    }
    if (!pendingNavigationReconciliationsRef.current.delete(selectionContextKey)) return

    let active = true
    void (async () => {
      try {
        await getQueryClient().refetchQueries(
          {queryKey: contentQueryKey, exact: true, type: 'all'},
          {throwOnError: true},
        )
      } catch {
        if (active) reportBulkActionError('Pull requests updated, but the results could not be refreshed')
      }
    })()
    return () => {
      active = false
    }
  }, [contentQueryKey, optimisticCloseEnabled, reportBulkActionError, selectionContextKey])
  const restoreSelectionForContext = useCallback(
    (
      contextKey: string,
      membershipKey: string,
      pullNumbers: number[],
      retiringBatchId?: number,
      authoritativePullNumbers?: ReadonlySet<number>,
      restoredSelectionKey?: string,
      restoredCurrentView?: {contextKey: string; pullNumbers: ReadonlySet<number>; selectionKey: string},
      deferUntilContextReturns = false,
    ) => {
      const currentView = currentSelectionViewRef.current
      const currentViewWasRefreshed = restoredCurrentView?.contextKey === currentView.contextKey
      const currentAuthoritativePullNumbers =
        (currentViewWasRefreshed ? restoredCurrentView?.pullNumbers : undefined) ??
        (currentView.contextKey === contextKey && authoritativePullNumbers
          ? authoritativePullNumbers
          : currentView.authoritativePullNumbers)
      const restorablePullNumbers =
        currentView.contextKey === contextKey && currentView.membershipContextKey === membershipKey
          ? pullNumbers.filter(
              pullNumber =>
                currentAuthoritativePullNumbers.has(pullNumber) &&
                !currentView.optimisticCloseBatches.some(
                  batch =>
                    batch.id !== retiringBatchId &&
                    batch.selectionContextKey === contextKey &&
                    batch.pullNumbers.has(pullNumber),
                ),
            )
          : []
      if (restorablePullNumbers.length > 0) {
        const currentSelectionKey =
          (currentViewWasRefreshed ? restoredCurrentView?.selectionKey : undefined) ??
          (currentView.contextKey === contextKey && restoredSelectionKey
            ? restoredSelectionKey
            : currentView.selectionKey)
        setSelection(current => ({
          key: currentSelectionKey,
          pullNumbers: new Set([
            ...(current.key === currentSelectionKey ? current.pullNumbers : []),
            ...restorablePullNumbers,
          ]),
        }))
      }
      if (!deferUntilContextReturns && currentView.contextKey === contextKey) return

      const restoredPullNumberSet = new Set(restorablePullNumbers)
      const pendingPullNumbers = deferUntilContextReturns
        ? pullNumbers
        : pullNumbers.filter(pullNumber => !restoredPullNumberSet.has(pullNumber))
      if (pendingPullNumbers.length === 0) return
      const pending = pendingSelectionRestorationsRef.current.get(contextKey) ?? new Set<number>()
      for (const pullNumber of pendingPullNumbers) pending.add(pullNumber)
      pendingSelectionRestorationsRef.current.set(contextKey, pending)
    },
    [],
  )
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Async mutation state must wait for the router context to settle.
    if (navigationSelectionContextKey && navigationSelectionContextKey !== selectionContextKey) return
    const pendingPullNumbers = pendingSelectionRestorationsRef.current.get(selectionContextKey)
    if (!pendingPullNumbers) return
    pendingSelectionRestorationsRef.current.delete(selectionContextKey)
    restoreSelectionForContext(selectionContextKey, membershipContextKey, [...pendingPullNumbers])
  }, [
    membershipContextKey,
    navigationSelectionContextKey,
    restoreSelectionForContext,
    selectionContextKey,
    visiblePulls,
  ])
  const selectFailedPullNumbersForContext = useCallback(
    (contextKey: string, pullNumbers: number[], deferUntilContextReturns = false) => {
      const currentView = currentSelectionViewRef.current
      if (!deferUntilContextReturns && currentView.contextKey === contextKey) {
        setSelection({
          key: currentView.selectionKey,
          pullNumbers: new Set(
            pullNumbers.filter(
              pullNumber =>
                currentView.authoritativePullNumbers.has(pullNumber) &&
                !currentView.optimisticCloseBatches.some(
                  batch => batch.selectionContextKey === contextKey && batch.pullNumbers.has(pullNumber),
                ),
            ),
          ),
        })
        return
      }

      const pendingPullNumbers = pendingSelectionRestorationsRef.current.get(contextKey) ?? new Set<number>()
      for (const pullNumber of pullNumbers) pendingPullNumbers.add(pullNumber)
      pendingSelectionRestorationsRef.current.set(contextKey, pendingPullNumbers)
    },
    [],
  )
  const bulkUpdateMutation = useRepoPullsBulkUpdate()
  const [bulkUpdatePendingContexts, setBulkUpdatePendingContexts] = useState(
    () => new Map<number, {contextKey: string; optimisticClose: boolean}>(),
  )
  const pendingBulkUpdates = [...bulkUpdatePendingContexts.values()]
  const hasPendingBulkUpdate = pendingBulkUpdates.length > 0
  const bulkUpdateControlsDisabled = pendingBulkUpdates.some(
    operation => operation.contextKey === selectionContextKey || !operation.optimisticClose,
  )
  const handleBulkUpdate = useCallback(
    (update: RepoPullsBulkUpdate) => {
      const operationId = ++bulkUpdateOperationIdRef.current
      const abortController = new AbortController()
      bulkUpdateAbortControllersRef.current.add(abortController)
      const submittedPullNumbers = [...selectedPullNumberList]
      const submittedSelectionKey = selectionKey
      const submittedPulls = selectedPulls.flatMap(pull =>
        pull.globalId ? [{nodeId: pull.globalId, number: pull.number}] : [],
      )
      const submittedMembershipContextKey = membershipContextKey
      const submittedSelectionContextKey = selectionContextKey
      const optimisticallyHideSubmittedPulls =
        optimisticCloseEnabled && 'state' in update && update.state === 'closed' && selectedState === 'open'
      setBulkUpdatePendingContexts(contexts =>
        new Map(contexts).set(operationId, {
          contextKey: submittedSelectionContextKey,
          optimisticClose: optimisticallyHideSubmittedPulls,
        }),
      )
      const optimisticCloseBatchId = optimisticallyHideSubmittedPulls ? ++optimisticCloseBatchIdRef.current : undefined
      if (optimisticallyHideSubmittedPulls && optimisticCloseBatchId !== undefined) {
        setOptimisticCloseBatches(batches => [
          ...batches,
          {
            id: optimisticCloseBatchId,
            membershipContextKey: submittedMembershipContextKey,
            selectionContextKey: submittedSelectionContextKey,
            pullNumbers: new Set(submittedPullNumbers),
          },
        ])
        setSelection({key: submittedSelectionKey, pullNumbers: new Set()})
        restoreSelectionFocusIfLost()
      }
      const progressMessage = `Updating ${submittedPullNumbers.length} pull request${submittedPullNumbers.length === 1 ? '' : 's'}`
      let latestPercentage = 0
      bulkUpdateProgressRef.current.set(operationId, {abortController, message: progressMessage})
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addPersistedToast({message: progressMessage, type: 'info'})
      announce(progressMessage)
      const clearPendingContext = () =>
        setBulkUpdatePendingContexts(contexts => {
          const next = new Map(contexts)
          next.delete(operationId)
          return next
        })
      const restoreSubmittedSelection = (pullNumbers = submittedPullNumbers) => {
        if (!optimisticallyHideSubmittedPulls) return
        removeOptimisticCloseBatch(optimisticCloseBatchId)
        restoreSelectionForContext(
          submittedSelectionContextKey,
          submittedMembershipContextKey,
          pullNumbers,
          optimisticCloseBatchId,
        )
      }
      const finishProgress = () => {
        if (!bulkUpdateProgressRef.current.delete(operationId)) return

        const remainingProgress = [...bulkUpdateProgressRef.current.entries()]
          .filter(([, progress]) => !progress.abortController.signal.aborted)
          .sort(([leftId], [rightId]) => rightId - leftId)[0]?.[1]
        if (!remainingProgress) {
          clearPersistedToast()
          return
        }

        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          message:
            remainingProgress.percentage !== undefined
              ? `Bulk update is in progress ${remainingProgress.percentage} %`
              : remainingProgress.message,
          icon:
            remainingProgress.percentage === undefined ? undefined : (
              <PercentageCircle progress={remainingProgress.percentage / 100} />
            ),
          type: 'info',
          role: remainingProgress.percentage === undefined ? undefined : 'status',
        })
      }

      void (async () => {
        try {
          const {failedPullNodeIds} = await bulkUpdateMutation.mutateAsync({
            owner,
            repo,
            pullNumbers: submittedPullNumbers,
            pullNodeIds: submittedPulls.map(pull => pull.nodeId),
            update,
            signal: abortController.signal,
            onProgress: percentage => {
              if (percentage <= latestPercentage) return
              latestPercentage = percentage
              const progress = bulkUpdateProgressRef.current.get(operationId)
              if (progress) progress.percentage = percentage
              const latestActiveOperationId = Math.max(
                ...[...bulkUpdateProgressRef.current.entries()]
                  .filter(([, activeProgress]) => !activeProgress.abortController.signal.aborted)
                  .map(([activeOperationId]) => activeOperationId),
              )
              if (latestActiveOperationId !== operationId) return
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addPersistedToast({
                message: `Bulk update is in progress ${percentage} %`,
                icon: <PercentageCircle progress={percentage / 100} />,
                type: 'info',
                role: 'status',
              })
            },
          })
          if (abortController.signal.aborted) {
            restoreSubmittedSelection()
            return
          }
          const failedNodeIdSet = new Set(failedPullNodeIds)
          const failedPullNumbers = submittedPulls
            .filter(pull => failedNodeIdSet.has(pull.nodeId))
            .map(pull => pull.number)
          const successfulPullNumbers = submittedPullNumbers.filter(
            pullNumber => !failedPullNumbers.includes(pullNumber),
          )
          let refreshedOriginPullNumbers: Set<number> | undefined
          let refreshedOriginSelectionKey: string | undefined
          let refreshedCurrentView:
            | {
                contextKey: string
                pullNumbers: Set<number>
                selectionKey: string
              }
            | undefined
          if ('state' in update && update.state === 'open') {
            removePullNumbersFromOptimisticCloseBatches(successfulPullNumbers)
          }
          const failedCount = failedPullNumbers.length
          const partialFailureMessage =
            failedCount > 0
              ? `Could not update ${failedCount} of ${submittedPullNumbers.length} pull request${submittedPullNumbers.length === 1 ? '' : 's'}`
              : null
          let refreshFailed = false
          try {
            await getQueryClient().refetchQueries(
              {queryKey: contentQueryKey, exact: true, type: 'all'},
              {throwOnError: true},
            )
            const refreshedOrigin = getQueryClient().getQueryData<RepoPullsContentCacheData>(contentQueryKey)
            if (refreshedOrigin?.payload?.results) {
              const authoritativePullNumbers = new Set(
                refreshedOrigin.payload.results.flatMap(pull => (pull.number === undefined ? [] : [pull.number])),
              )
              refreshedOriginPullNumbers = authoritativePullNumbers
              refreshedOriginSelectionKey = `${submittedSelectionContextKey}:${[...authoritativePullNumbers]
                .sort((left, right) => left - right)
                .join(',')}`
              if (currentSelectionViewRef.current.contextKey === submittedSelectionContextKey) {
                refreshedCurrentView = {
                  contextKey: submittedSelectionContextKey,
                  pullNumbers: authoritativePullNumbers,
                  selectionKey: refreshedOriginSelectionKey,
                }
              }
            }
          } catch {
            refreshFailed = true
          }

          if (abortController.signal.aborted) {
            restoreSubmittedSelection()
            return
          }

          let navigationTimedOut = false
          if (optimisticCloseEnabled) {
            const navigationCommitDeadline = Date.now() + PENDING_NAVIGATION_COMMIT_TIMEOUT_MS
            while (!abortController.signal.aborted) {
              const pendingNavigationContextKey = navigationSelectionContextKeyRef.current
              if (
                !pendingNavigationContextKey ||
                currentSelectionViewRef.current.contextKey === pendingNavigationContextKey
              ) {
                break
              }
              if (Date.now() >= navigationCommitDeadline) {
                navigationTimedOut = true
                pendingNavigationReconciliationsRef.current.add(pendingNavigationContextKey)
                break
              }
              await Promise.race([
                new Promise<void>(resolve => requestAnimationFrame(() => resolve())),
                new Promise<void>(resolve => setTimeout(resolve, 50)),
              ])
            }
          }
          if (abortController.signal.aborted) {
            restoreSubmittedSelection()
            return
          }

          const currentView = currentSelectionViewRef.current
          if (optimisticCloseEnabled && currentView.contextKey !== submittedSelectionContextKey) {
            try {
              await getQueryClient().refetchQueries(
                {queryKey: currentView.contentQueryKey, exact: true, type: 'all'},
                {throwOnError: true},
              )
              const refreshedDestination = getQueryClient().getQueryData<RepoPullsContentCacheData>(
                currentView.contentQueryKey,
              )
              if (refreshedDestination?.payload?.results) {
                const pullNumbers = new Set(
                  refreshedDestination.payload.results.flatMap(pull =>
                    pull.number === undefined ? [] : [pull.number],
                  ),
                )
                refreshedCurrentView = {
                  contextKey: currentView.contextKey,
                  pullNumbers,
                  selectionKey: `${currentView.contextKey}:${[...pullNumbers]
                    .sort((left, right) => left - right)
                    .join(',')}`,
                }
              }
            } catch {
              refreshFailed = true
            }
          }

          if (refreshFailed) {
            if (abortController.signal.aborted) {
              restoreSubmittedSelection()
              return
            }
            if (optimisticallyHideSubmittedPulls) {
              settleOptimisticCloseBatch(
                optimisticCloseBatchId,
                submittedPullNumbers.filter(pullNumber => !failedPullNumbers.includes(pullNumber)),
              )
              restoreSelectionForContext(
                submittedSelectionContextKey,
                submittedMembershipContextKey,
                failedPullNumbers,
                optimisticCloseBatchId,
                refreshedOriginPullNumbers,
                refreshedOriginSelectionKey,
                refreshedCurrentView,
                navigationTimedOut,
              )
            } else if (partialFailureMessage) {
              selectFailedPullNumbersForContext(submittedSelectionContextKey, failedPullNumbers, navigationTimedOut)
            }
            reportBulkActionError(
              partialFailureMessage ?? 'Pull requests updated, but the results could not be refreshed',
            )
            if (currentSelectionViewRef.current.contextKey === submittedSelectionContextKey) {
              restoreSelectionFocusIfLost()
            }
            return
          }

          await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
          if (abortController.signal.aborted) {
            restoreSubmittedSelection()
            return
          }

          if (partialFailureMessage) {
            if (optimisticallyHideSubmittedPulls) {
              settleOptimisticCloseBatch(
                optimisticCloseBatchId,
                submittedPullNumbers.filter(pullNumber => !failedPullNumbers.includes(pullNumber)),
              )
              restoreSelectionForContext(
                submittedSelectionContextKey,
                submittedMembershipContextKey,
                failedPullNumbers,
                optimisticCloseBatchId,
                refreshedOriginPullNumbers,
                refreshedOriginSelectionKey,
                refreshedCurrentView,
                navigationTimedOut,
              )
            } else {
              selectFailedPullNumbersForContext(submittedSelectionContextKey, failedPullNumbers, navigationTimedOut)
            }
            if (currentSelectionViewRef.current.contextKey === submittedSelectionContextKey) {
              restoreSelectionFocusIfLost()
            }
            reportBulkActionError(partialFailureMessage)
            return
          }
          settleOptimisticCloseBatch(optimisticCloseBatchId, submittedPullNumbers)

          if (currentSelectionViewRef.current.contextKey === submittedSelectionContextKey) {
            restoreSelectionFocusIfLost()
          }
          const successMessage = `${submittedPullNumbers.length} pull request${submittedPullNumbers.length === 1 ? '' : 's'} updated`
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({message: successMessage, type: 'success'})
          announce(successMessage)
        } catch (mutationError) {
          if (
            abortController.signal.aborted ||
            (mutationError instanceof Error && mutationError.name === 'AbortError')
          ) {
            restoreSubmittedSelection()
            return
          }
          restoreSubmittedSelection()
          finishProgress()
          reportBulkActionError('Could not update pull requests')
          try {
            await getQueryClient().refetchQueries(
              {queryKey: contentQueryKey, exact: true, type: 'all'},
              {throwOnError: true},
            )
          } catch {
            // The update error is primary; a failed best-effort refresh does not replace it.
          }
          if (abortController.signal.aborted) return
          if (currentSelectionViewRef.current.contextKey === submittedSelectionContextKey) {
            restoreSelectionFocusIfLost()
          }
        }
      })().finally(() => {
        clearPendingContext()
        bulkUpdateAbortControllersRef.current.delete(abortController)
        finishProgress()
      })
    },
    [
      addPersistedToast,
      addToast,
      bulkUpdateAbortControllersRef,
      bulkUpdateOperationIdRef,
      bulkUpdateProgressRef,
      bulkUpdateMutation,
      clearPersistedToast,
      contentQueryKey,
      membershipContextKey,
      owner,
      repo,
      reportBulkActionError,
      restoreSelectionFocusIfLost,
      selectFailedPullNumbersForContext,
      selectedPullNumberList,
      selectedPulls,
      selectionKey,
      selectionContextKey,
      optimisticCloseEnabled,
      removeOptimisticCloseBatch,
      removePullNumbersFromOptimisticCloseBatches,
      restoreSelectionForContext,
      settleOptimisticCloseBatch,
      selectedState,
    ],
  )
  const commonLabels = useMemo(() => {
    const firstPull = selectedPulls[0]
    if (!firstPull) return []
    return firstPull.labels
      .filter(label => selectedPulls.every(pull => pull.labels.some(candidate => candidate.id === label.id)))
      .map(label => ({id: String(label.id), name: label.name}))
  }, [selectedPulls])
  const {commonAssigneeLogins, partiallyAssignedAssignees} = useMemo(() => {
    const assigneesByLogin = new Map<string, {avatarUrl: string; count: number}>()
    for (const pull of selectedPulls) {
      for (const assignee of pull.assignees ?? []) {
        const current = assigneesByLogin.get(assignee.login)
        assigneesByLogin.set(assignee.login, {
          avatarUrl: assignee.avatarUrl,
          count: (current?.count ?? 0) + 1,
        })
      }
    }

    const common: string[] = []
    const partial: Array<{avatarUrl: string; login: string}> = []
    for (const [login, assignee] of assigneesByLogin) {
      if (assignee.count === selectedPulls.length) common.push(login)
      else partial.push({avatarUrl: assignee.avatarUrl, login})
    }

    return {commonAssigneeLogins: common, partiallyAssignedAssignees: partial}
  }, [selectedPulls])
  const canMarkAsOpen = selectedPulls.every(pull => pull.state !== 'MERGED')
  const showMarkAsClosed = selectedPulls.some(pull => pull.state === 'OPEN')
  const showMarkAsOpen = selectedPulls.some(pull => pull.state !== 'OPEN')
  const canOptimisticallyCloseSelection = optimisticCloseEnabled && selectedState === 'open'
  const bulkActions = useMemo<PullsListSelection['actions']>(() => {
    if (!canBulkUpdate || !owner || !repo || selectedPullNumberList.length === 0) return undefined
    const actions: NonNullable<PullsListSelection['actions']> = [
      {
        key: 'mark-as',
        render: nested => (
          <RepoPullsBulkMarkAs
            canArchive={canBulkArchive}
            canMarkAsOpen={canMarkAsOpen}
            disabled={bulkUpdateControlsDisabled || (hasPendingBulkUpdate && !showMarkAsClosed)}
            disableCloseOption={
              hasSelectedPullWithActiveClose || (hasPendingBulkUpdate && !canOptimisticallyCloseSelection)
            }
            disableNonCloseOptions={hasPendingBulkUpdate}
            nested={nested}
            onUpdate={handleBulkUpdate}
            showMarkAsClosed={showMarkAsClosed}
            showMarkAsOpen={showMarkAsOpen}
          />
        ),
      },
      {
        key: 'labels',
        render: nested => (
          <RepoPullsBulkLabels
            commonLabels={commonLabels}
            disabled={hasPendingBulkUpdate}
            nested={nested}
            owner={owner}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      },
    ]
    if (canBulkAddToProjects) {
      actions.push({
        key: 'projects',
        render: nested => (
          <RepoPullsBulkProjects
            disabled={hasPendingBulkUpdate}
            nested={nested}
            owner={owner}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      })
    }
    actions.push(
      {
        key: 'milestone',
        render: nested => (
          <RepoPullsBulkMilestone
            disabled={hasPendingBulkUpdate}
            nested={nested}
            owner={owner}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      },
      {
        key: 'assignees',
        render: nested => (
          <RepoPullsBulkAssignees
            commonAssigneeLogins={commonAssigneeLogins}
            disabled={hasPendingBulkUpdate}
            nested={nested}
            owner={owner}
            partiallyAssignedAssignees={partiallyAssignedAssignees}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      },
    )
    return actions
  }, [
    canBulkAddToProjects,
    canBulkArchive,
    canBulkUpdate,
    canMarkAsOpen,
    canOptimisticallyCloseSelection,
    commonAssigneeLogins,
    commonLabels,
    bulkUpdateControlsDisabled,
    hasSelectedPullWithActiveClose,
    hasPendingBulkUpdate,
    handleBulkUpdate,
    owner,
    partiallyAssignedAssignees,
    repo,
    selectedPullNumberList,
    showMarkAsClosed,
    showMarkAsOpen,
  ])

  function handleFilterSelect(type: 'repo' | 'author' | 'label' | 'assignee', value: string) {
    let nextQuery = fetchedQuery
    switch (type) {
      case 'repo':
        return
      case 'author':
        nextQuery = addAuthorFilterToQuery(fetchedQuery, value)
        break
      case 'label':
        nextQuery = addLabelFilterToQuery(fetchedQuery, value)
        break
      case 'assignee':
        nextQuery = addAssigneeFilterToQuery(fetchedQuery, value)
        break
    }
    applyQueryFilter({query: nextQuery, filter: type, source: 'list'})
  }

  function getAuthorLinkProps(login: string) {
    const nextQuery = addAuthorFilterToQuery(fetchedQuery, login)
    return {
      as: PreloadableLink,
      to: hrefForQuery(nextQuery),
      reloadDocument: false,
      state: {preserveList: true},
      onClick: hasSameFilterState(fetchedQuery, nextQuery, 'author', structuralUpdatesEnabled)
        ? undefined
        : () => sendFilterAnalytics('author', 'list'),
    }
  }

  function getStateFilterLinkProps(state: StateFilter) {
    return {
      to: hrefForQuery(addRepoStateToQuery(fetchedQuery, state)),
      reloadDocument: false,
    }
  }

  return (
    <div>
      <div className="tmp-mt-2 tmp-mb-3" {...hpcAttrs()}>
        {error ? (
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        ) : (
          <>
            <PullsList
              pulls={isListLoading ? EMPTY_PULLS : visiblePulls}
              pageInfo={visiblePageInfo}
              compactMode={compactMode}
              onCompactModeChange={updateCompactModeSetting}
              onSortingItemSelect={sort =>
                applyQueryFilter({query: addSortToQuery(fetchedQuery, sort), filter: 'sort', source: 'toolbar'})
              }
              sortingItemSelected={getSortFromQuery(fetchedQuery)}
              stateFilters={
                stateQueryAnalysis.kind === 'ambiguous'
                  ? {kind: 'hidden'}
                  : {
                      kind: 'visible',
                      selected: stateQueryAnalysis.kind === 'selected' ? stateQueryAnalysis.state : undefined,
                      counts: visibleStateCounts,
                      getLinkProps: getStateFilterLinkProps,
                    }
              }
              emptyListState={emptyListState}
              onFilterSelect={handleFilterSelect}
              onReviewFilterSelect={value =>
                applyQueryFilter({
                  query: replaceReviewFilter(fetchedQuery, {key: 'review', value}, structuralUpdatesEnabled),
                  filter: 'reviews',
                  source: 'list',
                })
              }
              isLoading={isListLoading}
              isDeferredLoading={isDeferredLoading}
              suppressEmptyState={hiddenPullCount > 0}
              isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
              showUnreadIndicator={isLoggedIn()}
              defaultSortingKey={REPO_PULLS_DEFAULT_SORTING_KEY}
              showRepository={false}
              actionBarItems={owner && repo ? quickFilterItems : undefined}
              getTitleLinkProps={getPullRequestTitleLinkProps}
              selection={
                canBulkUpdate
                  ? {
                      selectedPullNumbers,
                      disabled: isNavigating || bulkUpdateControlsDisabled || visiblePulls.length === 0,
                      selectAllCheckboxRef,
                      onPullSelect: handlePullSelect,
                      onToggleSelectAll: handleToggleSelectAll,
                      actions: bulkActions,
                    }
                  : undefined
              }
              getAuthorLinkProps={getAuthorLinkProps}
            />
            {!isNavigating && pageInfo.totalPages > 1 ? (
              <Pagination
                pageCount={pageInfo.totalPages}
                currentPage={pageInfo.currentPage}
                hrefBuilder={getPageHref}
                renderPage={({key, number, href, ...props}) => (
                  <PreloadableLink key={key} {...props} to={href ?? getPageHref(number)} preload="intent" />
                )}
                onPageChange={(event, page) => {
                  event.preventDefault()
                  navigateToResults({q: fetchedQuery, page})
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
